import { useQuery } from '@tanstack/react-query'
import { useAppStore } from '../stores/appStore'
import { buildApiUrl } from '../config'

interface Accessory {
  id: number
  title: string
  slug: string
  product_thumbnail?: string
  order: number
  three_d_model?: {
    id: number
    file_url: string
    preview_url?: string
  } | null
  texture_options: Array<{
    slot_name: string
    slot_label: string
    textures: Array<{
      id: number
      name: string
      base_color_url?: string
      normal_url?: string
      roughness_url?: string
      metalness_url?: string
      ambient_occlusion_url?: string
    }>
  }>
}

interface AccessoriesFetcherProps {
  bedId: string
}

export default function AccessoriesFetcher({ bedId }: AccessoriesFetcherProps) {
  const { market, lang, bedConfig, toggleAccessory } = useAppStore()

  const { data: accessoriesData, isLoading, error } = useQuery({
    queryKey: ['accessories', bedId, market, lang],
    queryFn: async () => {
      const url = buildApiUrl(`bed-variations/${bedId}/lamps/?market=${market}&lang=${lang}`)
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error('Failed to fetch accessories')
      }
      const data = await response.json()
      return data
    },
    enabled: !!bedId,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  })

  const accessories = accessoriesData?.options || []
  const selectedAccessories = bedConfig.accessories

  const handleAccessoryClick = (accessory: Accessory) => {
    const accessoryComponent = {
      id: accessory.id,
      name: accessory.title,
      slug: accessory.slug,
      image_url: accessory.product_thumbnail,
      three_d_model: accessory.three_d_model,
      texture_options: accessory.texture_options,
      currentTexture: accessory.texture_options[0]?.textures[0] || null,
    }
    toggleAccessory(accessoryComponent)
  }

  const isSelected = (accessory: Accessory) => {
    return selectedAccessories.some(acc => acc.id === accessory.id)
  }

  if (isLoading) {
    return <div className="p-4">Loading accessories...</div>
  }

  if (error) {
    return <div className="p-4">Error loading accessories: {error.message}</div>
  }

  if (accessories.length === 0) {
    return <div className="p-4">No accessories available (bedId: {bedId}, market: {market}, lang: {lang})</div>
  }

  return (
    <div className="w-full overflow-x-auto px-6 snap-x snap-mandatory">
      <div className="flex gap-2 w-max">
        {accessories.map((accessory) => (
          <div
            key={accessory.id}
            className={`snap-center cursor-pointer`}
            onClick={() => handleAccessoryClick(accessory)}
          >
            {accessory.product_thumbnail && (
              <img
                src={accessory.product_thumbnail}
                alt={accessory.title}
                className={`aspect-square w-36 mb-2 ${isSelected(accessory) ? 'border-2 border-white outline outline-1 outline-black' : ''}`}
              />
            )}
            <div className="text-center">
              <h4 className="text-xs">{accessory.title}</h4>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
